import { X, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";
import { MEDIA_LIMITS } from "@/lib/media-limits";
import { formatFileSize } from "@/lib/media-prep";
import { cn } from "@/lib/utils";

type FilePreviewItem = {
  file: File;
  previewUrl?: string;
};

type FileAttachmentPreviewProps = {
  files: FilePreviewItem[];
  onRemove: (index: number) => void;
  maxFiles?: number;
  className?: string;
};

function getFileIcon(type: string) {
  if (type.startsWith("image/")) return ImageIcon;
  if (type === "application/pdf") return FileText;
  return FileIcon;
}

export function FileAttachmentPreview({
  files,
  onRemove,
  maxFiles = MEDIA_LIMITS.maxAttachments,
  className,
}: FileAttachmentPreviewProps) {
  if (files.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap gap-2 px-4 pb-2", className)}>
      {files.slice(0, maxFiles).map((item, i) => {
        const Icon = getFileIcon(item.file.type);
        const isImage = item.file.type.startsWith("image/");

        return (
          <div
            key={`${item.file.name}-${i}`}
            className={cn(
              "group relative flex items-center gap-2 px-3 py-2 rounded-xl",
              "bg-app-surface-muted border border-app-border",
              "text-sm transition-all duration-150",
              "hover:border-brand hover:bg-app-surface"
            )}
          >
            {/* Thumbnail or icon */}
            {isImage && item.previewUrl ? (
              <img
                src={item.previewUrl}
                alt={item.file.name}
                className="w-8 h-8 rounded-md object-cover shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-md bg-brand-soft flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-brand" />
              </div>
            )}

            {/* File info */}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-app-fg-strong truncate max-w-[140px]">
                {item.file.name}
              </p>
              <p className="text-[10px] text-app-fg-muted">
                {formatFileSize(item.file.size)}
              </p>
            </div>

            {/* Remove button */}
            <button
              type="button"
              onClick={() => onRemove(i)}
              aria-label={`Remover ${item.file.name}`}
              className={cn(
                // The target is 44 px (40 px from sm up); the visible mark stays a 20 px dot.
                "absolute -top-[18px] -right-[18px] flex size-11 items-center justify-center rounded-full sm:-top-4 sm:-right-4 sm:size-10",
                "opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-150",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-app-danger"
              )}
            >
              <span className="flex size-5 items-center justify-center rounded-full bg-app-danger text-app-surface hover:brightness-110">
                <X className="w-3 h-3" aria-hidden="true" />
              </span>
            </button>
          </div>
        );
      })}

      {files.length > maxFiles && (
        <div className="flex items-center px-3 py-2 text-xs text-app-fg-muted">
          +{files.length - maxFiles} arquivo(s) ignorado(s)
        </div>
      )}
    </div>
  );
}
